// Sesi admin: profil + menu yang diizinkan (sidebar dan tombol aksi mengikuti izin).
import type { AdminActionCode, AdminMenuCode } from '@folaplus/contracts';
import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';

import { api, ApiError, type ResponseOf } from './api';

interface Session {
  me: ResponseOf<'me'>;
  menus: ResponseOf<'menus'>;
  isViewMode: boolean;
  /** Tombol aksi hanya tampil bila izin ada; View Mode tidak pernah punya aksi selain baca. */
  can: (menu: AdminMenuCode, action: AdminActionCode) => boolean;
}

const Context = createContext<Session | null>(null);

export function useSessionQuery() {
  return useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const [me, menus] = await Promise.all([api('me'), api('menus')]);
        return { me, menus };
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
  });
}

export function SessionProvider({
  value,
  children,
}: {
  value: { me: ResponseOf<'me'>; menus: ResponseOf<'menus'> };
  children: ReactNode;
}) {
  const isViewMode = value.me.role.isViewMode;
  const session: Session = {
    ...value,
    isViewMode,
    can: (menu, action) => {
      if (isViewMode && action !== 'READ') return false;
      return value.menus.some((m) => m.menu === menu && m.actions.includes(action));
    },
  };
  return <Context.Provider value={session}>{children}</Context.Provider>;
}

export function useSession(): Session {
  const session = useContext(Context);
  if (!session) throw new Error('useSession di luar SessionProvider');
  return session;
}
